import { useEffect, useId, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import { useTranslation } from "react-i18next";
import { STAGES } from "../utils/stages";
import PromotionCandidateList from "./PromotionCandidateList";
import "./PromotionStageBoard.css";

/** Stage data and candidate eligibility are supplied by the page, not inferred here. */
// fit: alle Spalten ohne waagerechtes Scrollen, bei schmaler Breite umbrechen statt quetschen (Stufenbranches, Vertrag 27.1).
// Fachlicher Text eines Stufenfehlers; Texte mit offenen Platzhaltern ({{...}}) oder ohne Eintrag zeigen nie roh,
// stattdessen „nicht verfügbar“.
const stageErrorText = (t, i18n, code, details) => {
  const key = `stageBranches.errors.${code}`;
  // returnEmptyString: false der App liefert für leere Texte den Schlüssel selbst, daher exists und Gleichheit prüfen.
  const text = i18n.exists(key, { ns: "freigabe" }) ? t(key, details && typeof details === "object" ? details : {}) : "";
  return text && text !== key && !/\{\{.*\}\}/.test(text) ? text : t("promotion.stageView.unavailable");
};

export default function PromotionStageBoard({ stages = [], candidates = [], renderCandidate, renderSelection, renderStageActivity, searchActive = false, search = "", includeDev = false, inventory = false, dropTarget = null, dropOver = false, fit = false }) {
  const { t, i18n } = useTranslation("freigabe");
  const id = useId();
  const stageData = new Map(stages.map(stage => [stage.key, stage]));
  const uniqueCandidates = [...new Map(candidates.map(candidate => [candidate.id, candidate])).values()];
  const columns = STAGES.filter(stage => includeDev || stage.key !== "dev");
  const gridRef = useRef(null);
  const [layout, setLayout] = useState("row");

  useEffect(() => {
    if (fit) return undefined;
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") { setLayout("row"); return undefined; }
    const minRequired = columns.length * 280 + (columns.length - 1) * 12;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width ?? 0;
      setLayout(width >= minRequired ? "row" : "scroll");
    });
    observer.observe(grid);
    return () => observer.disconnect();
  }, [columns.length, fit]);

  return <Box component="section" className={`promotion-stage-board${dropTarget ? " is-dragging" : ""}`} aria-label={t("promotion.stageView.label")}>
    <Box ref={gridRef} className="promotion-stage-board-grid" data-layout={fit ? "fit" : layout} style={{ "--promotion-stage-count": columns.length }}>
      {columns.map((definition, index) => {
        const stage = stageData.get(definition.key);
        const configured = stage?.configured === true;
        const unavailable = !stage || Boolean(stage.error) || typeof stage.configured !== "boolean"
          || (configured && !Array.isArray(stage.packages));
        const stageCandidates = configured && !unavailable
          ? uniqueCandidates.filter(candidate => candidate.sourceStage === definition.key) : [];
        const candidatePackages = new Set(stageCandidates.map(candidate => candidate.packageId));
        const packageCount = candidatePackages.size;
        const titleId = `${id}-${definition.key}`;
        // Mehrzielmodus: mehrere gezeigte Ziele ergeben Untergruppen je Ziel (Vertrag V.3).
        const multiTargets = Array.isArray(stage?.targets) && stage.targets.length > 1;
        const isDropTarget = dropTarget === definition.key;
        const activity = configured && !unavailable ? renderStageActivity?.(stage) : null;

        return <Paper component="section" elevation={0} variant="outlined" key={definition.key}
          className={`promotion-stage-lane promotion-stage-column promotion-stage-column--${definition.key}${configured ? "" : " is-unconfigured"}${isDropTarget ? " is-drop-target" : ""}${isDropTarget && dropOver ? " is-drop-over" : ""}`}
          data-stage={definition.key} aria-labelledby={titleId}>
          <Box component="header" className="promotion-stage-column-head">
            <span className="promotion-stage-ordinal" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
            <Typography component="h3" id={titleId} className="promotion-stage-title">{definition.label}</Typography>
            {configured && !unavailable && <Typography component="span" className="promotion-stage-count">
              {t("promotion.stageView.packageCount", { count: packageCount })}
            </Typography>}
            <Typography component="p" className="promotion-stage-instance">
              {unavailable ? t("promotion.stageView.unavailable")
                : configured ? (multiTargets ? t("stageBranches.targets.count", { count: stage.targets.length }) : stage.instanceName || t("promotion.stageView.instanceUnknown"))
                  : t("promotion.stageView.notConfigured")}
            </Typography>
            {configured && !unavailable && stageCandidates.length > 0 && renderSelection?.(stageCandidates, definition.label)}
          </Box>
          <Box className="promotion-stage-column-body">
            {activity}
            {configured && !unavailable && multiTargets ? stage.targets.map(target => {
              // Zeilen ohne targetId (etwa ältere Fehlerzeilen) gehören zur Standardzielgruppe; das Backend lässt targetId beim
              // Standardziel weg, fehlend heißt "default".
              const group = stageCandidates.filter(candidate => (candidate.targetId ?? "default") === target.targetId
                || (candidate.targetId == null && target.isDefault));
              return <Box component="section" key={target.targetId} className="promotion-stage-target" aria-label={target.instanceName}>
                <Typography component="h4" className="promotion-stage-target-head">
                  {target.instanceName}{target.region ? ` · ${target.region}` : ""}
                </Typography>
                {group.length > 0 ? <PromotionCandidateList candidates={group} stage={stage} renderCandidate={renderCandidate} resetKey={search}/>
                  : <Typography component="p" className="promotion-stage-target-empty">{t("stageBranches.targets.none")}</Typography>}
              </Box>;
            }) : configured && !unavailable && packageCount > 0 ? <PromotionCandidateList candidates={stageCandidates}
              stage={stage} renderCandidate={renderCandidate} resetKey={search}/> : activity ? null : <Box className="promotion-stage-empty">
              <Inventory2Outlined aria-hidden="true"/>
              {!unavailable && configured && <Typography component="strong" className="promotion-stage-empty-title">
                {t(searchActive ? "promotion.stageView.noMatches" : inventory ? "topics.stageEmpty" : "promotion.stageView.emptyTitle")}
              </Typography>}
              {/* Fachlicher Grund einer gesperrten Stufe (stages[].error, auch ohne Mehrzielmodus), sonst nur „nicht verfügbar“. */}
              {unavailable && typeof stage?.error === "string" && <Typography component="p" className="promotion-stage-empty-description" role="note">
                {stageErrorText(t, i18n, stage.error, stage.details)}
              </Typography>}
              {!unavailable && <Typography component="p" className="promotion-stage-empty-description">
                {t(configured ? searchActive ? "promotion.stageView.noMatchesDescription" : inventory ? "topics.stageEmptyHint" : "promotion.stageView.emptyDescription"
                  : "promotion.stageView.notConfiguredDescription")}
              </Typography>}
            </Box>}
            {isDropTarget && <Box className="promotion-stage-dropzone">
              <strong>{t("promotion.stageView.dropHere", { defaultValue: "Hier ablegen" })}</strong>
              <span>{t("promotion.stageView.dropHint", { stage: definition.label, defaultValue: "Freigabe für {{stage}} anfordern" })}</span>
            </Box>}
          </Box>
        </Paper>;
      })}
    </Box>
  </Box>;
}
